<script setup lang="ts">
import { ArrowUpRight, Building, GripVertical, UserRound, Users } from '@lucide/vue'
import { Avatar, AvatarFallback, Button, ZhConfirmButton, ZhPanel, ZhSectionHeading, ZhStatusPill } from '@zerohuman/ui'
import { RouterLink } from 'vue-router'
import type { AgentConnection, AgentUsage } from '@/api'
import AgentProviderLogo from '@/components/AgentProviderLogo.vue'
import AgentUsageMeters from '@/components/AgentUsageMeters.vue'
import { AGENT_PROVIDER_NAMES, agentAuthLabel, agentOwnerPath } from '@/lib/agents'
import { subscriptionLabel } from '@/lib/cost'

/**
 * `usage` and `usageLoading` come from the list: the panel reads every card's usage in one batched request
 * (OS-PORTAL-2), so a card never asks for its own.
 */
defineProps<{
    row: AgentConnection
    busy?: boolean
    showOwner?: boolean
    reorderable?: boolean
    dragging?: boolean
    displayPriority?: number
    usage?: AgentUsage | null
    usageLoading?: boolean
}>()

const emit = defineEmits<{
    edit: [row: AgentConnection]
    delete: [row: AgentConnection]
    dragstart: [row: AgentConnection, event: DragEvent]
    dragover: [row: AgentConnection, event: DragEvent]
    drop: [row: AgentConnection, event: DragEvent]
    dragend: [event: DragEvent]
}>()

function date(value: string) {
    return new Date(value).toLocaleString()
}
</script>

<template>
    <!--
        Each row is a kit panel of its own. Status reads through the kit pill: a credential that needs attention takes
        the warning tone (its ▲ points at the operator), a configured one the completed tone. A seat with a price takes
        the billing plan's tone, one still costed at list price the warning tone, so it reads as something to set.
    -->
    <ZhPanel
        as="li"
        class="flex flex-col gap-3 transition-opacity duration-150"
        :class="dragging ? 'opacity-35 shadow-sm' : ''"
        :data-connection-id="row.id"
        @dragover="emit('dragover', row, $event)"
        @drop="emit('drop', row, $event)"
    >
        <div class="flex flex-col items-start justify-between gap-4 sm:flex-row">
            <div class="flex w-full min-w-0 flex-1 items-start gap-3">
                <Avatar size="lg">
                    <AvatarFallback>
                        <AgentProviderLogo :provider="row.provider" :size="19" class="text-muted-foreground" />
                    </AvatarFallback>
                </Avatar>
                <div class="min-w-0 space-y-1.5">
                    <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <!-- A named connection leads with its name; provider and auth kind stay, one line down. -->
                        <ZhSectionHeading v-if="row.name">{{ row.name }}</ZhSectionHeading>
                        <ZhSectionHeading v-else>{{ AGENT_PROVIDER_NAMES[row.provider] }}
                            <span class="text-muted-foreground font-normal">· {{ agentAuthLabel(row) }}</span></ZhSectionHeading>
                        <ZhStatusPill :tone="row.status === 'unavailable' ? 'action' : 'completed'" :pulse="false">
                            {{ row.status === 'unavailable' ? 'Needs attention' : 'Configured' }}
                        </ZhStatusPill>
                    </div>
                    <p v-if="row.name" class="text-muted-foreground text-xs">
                        {{ AGENT_PROVIDER_NAMES[row.provider] }} · {{ agentAuthLabel(row) }}
                    </p>
                    <p v-if="row.description" class="text-muted-foreground text-sm">{{ row.description }}</p>
                    <p class="text-muted-foreground text-xs">Priority {{ displayPriority ?? row.priority }}</p>
                    <div v-if="row.authKind === 'oauth'">
                        <ZhStatusPill
                            data-testid="agent-seat-plan"
                            :tone="row.subscription?.monthlyUsdCents != null ? 'plan' : 'action'"
                            :pulse="false"
                        >
                            {{ subscriptionLabel(row.subscription) }}
                        </ZhStatusPill>
                    </div>
                    <RouterLink
                        v-if="showOwner"
                        :to="agentOwnerPath(row)"
                        class="text-muted-foreground hover:text-foreground inline-flex max-w-full items-center gap-1.5 text-xs"
                    >
                        <Building v-if="row.layer === 'enterprise'" :size="13" class="shrink-0" />
                        <Users v-else-if="row.layer === 'team'" :size="13" class="shrink-0" />
                        <UserRound v-else :size="13" class="shrink-0" />
                        <span class="break-words">{{ row.ownerName }}</span>
                        <span class="shrink-0">· {{ row.layer === 'enterprise' ? 'Enterprise' : row.layer === 'team' ? 'Team' : 'Member' }}</span>
                        <ArrowUpRight :size="12" class="shrink-0" />
                    </RouterLink>
                    <p v-if="row.status === 'unavailable'" class="text-muted-foreground text-xs">
                        This stored credential is unavailable. Edit the connection to replace it or connect again.
                    </p>
                    <p v-if="row.updatedAt" class="text-muted-foreground text-xs">
                        Updated <time :datetime="row.updatedAt">{{ date(row.updatedAt) }}</time>
                    </p>
                </div>
            </div>
            <div class="flex shrink-0 self-end gap-2 sm:self-start">
                <Button
                    v-if="reorderable"
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    draggable="true"
                    class="text-muted-foreground cursor-grab active:cursor-grabbing"
                    :aria-label="`Drag ${row.name ?? AGENT_PROVIDER_NAMES[row.provider]} to reorder priority`"
                    title="Drag to reorder priority"
                    @dragstart.stop="emit('dragstart', row, $event)"
                    @dragend="emit('dragend', $event)"
                >
                    <GripVertical :size="16" />
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    :disabled="busy"
                    :aria-label="`Edit ${row.name ?? AGENT_PROVIDER_NAMES[row.provider]} for ${row.ownerName}`"
                    @click="emit('edit', row)"
                >
                    Edit
                </Button>
                <ZhConfirmButton
                    label="Delete"
                    confirm-label="Remove connection"
                    :aria-label="`Remove ${row.name ?? AGENT_PROVIDER_NAMES[row.provider]} from ${row.ownerName}`"
                    :title="`Remove ${row.name ?? AGENT_PROVIDER_NAMES[row.provider]} from ${row.ownerName}?`"
                    description="Runs use the next eligible connection by priority. Runs with no remaining connection wait until one is connected."
                    :disabled="busy"
                    @confirm="emit('delete', row)"
                />
            </div>
        </div>
        <AgentUsageMeters :usage="usage ?? null" :loading="usageLoading" />
    </ZhPanel>
</template>
